import type { SxProps, Theme } from '@mui/material'
import type { AudioState, ConnectionState, CryptoState, NotificationState } from './types'
import { HostStatus } from '@/infra/sse/status/types'

export const STATUS_ICON_SIZE = 20
export const STATUS_ICON_SIZE_COLLAPSED = 18
export const STATUS_DOT_SIZE = 9

type ThemeColorResolver = (theme: Theme) => string

type ConnectionColorMap = Record<ConnectionState, ThemeColorResolver>
type NotificationColorMap = Record<NotificationState, ThemeColorResolver>
type AudioColorMap = Record<AudioState, ThemeColorResolver>

const CONNECTION_COLORS: ConnectionColorMap = {
  dormant: (theme) => theme.palette.text.disabled,
  initializing: (theme) => theme.palette.warning.main,
  error: (theme) => theme.palette.error.main,
  connecting: (theme) => theme.palette.primary.main,
  connected: (theme) => theme.palette.success.main,
  disconnected: (theme) => theme.palette.error.main,
}

const NOTIFICATION_COLORS: NotificationColorMap = {
  unsupported: (theme) => theme.palette.error.main,
  requesting: (theme) => theme.palette.warning.main,
  enabled: (theme) => (theme.palette.mode === 'dark' ? '#42a5f5' : theme.palette.info.main),
  blocked: (theme) => theme.palette.error.main,
  disabled: (theme) => theme.palette.text.disabled,
}

const AUDIO_COLORS: AudioColorMap = {
  ready: (theme) => (theme.palette.mode === 'dark' ? '#26c6da' : theme.palette.info.light),
  waiting: (theme) => theme.palette.warning.main,
  disabled: (theme) => theme.palette.text.disabled,
}

export const DOT_SIZES = {
  small: 6,
  medium: 6,
  large: 8,
} as const

export type StatusSize = keyof typeof DOT_SIZES

export function getConnectionColor(state: ConnectionState, theme: Theme): string {
  return CONNECTION_COLORS[state](theme)
}

export function getNotificationColor(state: NotificationState, theme: Theme): string {
  return NOTIFICATION_COLORS[state](theme)
}

export function getAudioColor(state: AudioState, theme: Theme): string {
  return AUDIO_COLORS[state](theme)
}

export function createPulseAnimation(color: string): SxProps<Theme> {
  return {
    '@keyframes statusPulse': {
      '0%': {
        opacity: 0.7,
        transform: 'scale(1)',
        boxShadow: `0 0 0 1px ${color}20`,
      },
      '50%': {
        opacity: 1,
        transform: 'scale(1.2)',
        boxShadow: `0 0 0 2px ${color}30`,
      },
      '100%': {
        opacity: 0.7,
        transform: 'scale(1)',
        boxShadow: `0 0 0 1px ${color}20`,
      },
    },
  }
}

export const CONNECTION_LABELS: Record<ConnectionState, string> = {
  dormant: 'Service dormant',
  initializing: 'Service initializing',
  error: 'Connection failed',
  connecting: 'Service connecting',
  connected: 'Connection on',
  disconnected: 'Connection off',
}

export const NOTIFICATION_LABELS: Record<NotificationState, string> = {
  unsupported: 'Notifications not supported',
  requesting: 'Requesting permission',
  enabled: 'Notifications on',
  blocked: 'Notifications blocked',
  disabled: 'Notifications off',
}

export const AUDIO_LABELS: Record<AudioState, string> = {
  ready: 'Sound on',
  waiting: 'Sound waiting',
  disabled: 'Sound off',
}

type CryptoColorMap = Record<CryptoState, ThemeColorResolver>

const CRYPTO_COLORS: CryptoColorMap = {
  loaded: (theme) => (theme.palette.mode === 'dark' ? '#ba68c8' : '#8E24AA'),
  missing: (theme) => theme.palette.warning.main,
}

export function getCryptoColor(state: CryptoState, theme: Theme): string {
  return CRYPTO_COLORS[state](theme)
}

export const CRYPTO_LABELS: Record<CryptoState, string> = {
  loaded: 'Encryption on',
  missing: 'Encryption off',
}

type HostStatusColorMap = Record<HostStatus, ThemeColorResolver>

const HOST_STATUS_COLORS: HostStatusColorMap = {
  [HostStatus.ALIVE]: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(102, 187, 106, 0.45)' : 'rgba(56, 142, 60, 0.55)',
  [HostStatus.DEAD]: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.45)' : 'rgba(211, 47, 47, 0.55)',
  [HostStatus.DORMANT]: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(62, 39, 35, 0.5)',
}

export function getHostStatusColor(status: HostStatus, theme: Theme): string {
  return HOST_STATUS_COLORS[status](theme)
}

export const HOST_STATUS_LABELS: Record<HostStatus, string> = {
  [HostStatus.ALIVE]: 'Host online',
  [HostStatus.DEAD]: 'Host offline',
  [HostStatus.DORMANT]: 'Host idle',
}

export const RESUME_CARD_PULSE_KEYFRAMES = {
  '@keyframes resumeCardPulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.6 },
  },
}

export const resumePulseSx: SxProps<Theme> = {
  ...RESUME_CARD_PULSE_KEYFRAMES,
  animation: 'resumeCardPulse 2s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}
